import { useState, useEffect } from 'react';
import { FileDown, QrCode, Printer, Globe, ExternalLink, Download, Check, Info } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

export const FlyerDownloadSection = ({ businessId }) => {
  const { t } = useTranslation();
  const [selectedLang, setSelectedLang] = useState('es');
  const [loading, setLoading] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState(null);

  useEffect(() => {
    fetchDownloadLinks();
  }, [businessId]);

  const fetchDownloadLinks = async () => {
    try {
      const response = await axios.get(`${API}/business/flyer/${businessId}/download-links`);
      setDownloadLinks(response.data);
    } catch (error) {
      console.error('Error fetching flyer links:', error);
    }
  };

  const handleViewFlyer = () => {
    const url = `${API}/business/flyer/${businessId}?lang=${selectedLang}`;
    window.open(url, '_blank');
  };

  const handleDownloadQR = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/business/flyer/${businessId}/qr`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr_${businessId}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('flyer.qr_downloaded', 'QR descargado'));
    } catch (error) {
      toast.error(t('flyer.download_error', 'Error al descargar'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintFlyer = () => {
    const url = `${API}/business/flyer/${businessId}?lang=${selectedLang}`;
    const printWindow = window.open(url, '_blank');
    printWindow.addEventListener('load', () => {
      printWindow.print();
    });
  };

  return (
    <div className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 rounded-2xl p-6 border border-emerald-500/20">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-emerald-500/20 rounded-xl">
          <FileDown className="w-6 h-6 text-emerald-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">
            {t('flyer.title', 'Flyer Promocional')}
          </h3>
          <p className="text-sm text-gray-400">
            {t('flyer.description', 'Descarga tu flyer con código QR para tus clientes')}
          </p>
        </div>
      </div>

      {/* Language Selector */}
      <div className="mb-4">
        <label className="text-sm text-gray-400 mb-2 block flex items-center gap-2">
          <Globe className="w-4 h-4" />
          {t('flyer.select_language', 'Selecciona idioma del flyer')}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setSelectedLang(lang.code)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                selectedLang === lang.code
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <span>{lang.flag}</span>
              <span className="hidden sm:inline">{lang.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Preview and Actions */}
      <div className="bg-gray-800/50 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-400">
            {t('flyer.preview', 'Vista previa del flyer')}
          </span>
          <div className="flex items-center gap-1 text-xs text-emerald-400">
            <Check className="w-3 h-3" />
            {t('flyer.qr_included', 'QR personalizado incluido')}
          </div>
        </div>
        
        {/* Mini preview placeholder */}
        <div className="aspect-[3/4] max-h-48 bg-gradient-to-b from-emerald-900 to-emerald-950 rounded-lg flex items-center justify-center mb-3 overflow-hidden">
          <div className="text-center p-4">
            <QrCode className="w-12 h-12 text-white/50 mx-auto mb-2" />
            <p className="text-white/70 text-sm">
              {LANGUAGES.find(l => l.code === selectedLang)?.flag} {LANGUAGES.find(l => l.code === selectedLang)?.name}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Button
            onClick={handleViewFlyer}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            {t('flyer.view', 'Ver Flyer')}
          </Button>
          
          <Button
            onClick={handleDownloadQR}
            variant="outline"
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            disabled={loading}
          >
            <Download className="w-4 h-4 mr-2" />
            {loading ? '...' : t('flyer.download_qr', 'Solo QR')}
          </Button>
          
          <Button
            onClick={handlePrintFlyer}
            variant="outline"
            className="border-gray-500/50 text-gray-300 hover:bg-gray-500/10"
          >
            <Printer className="w-4 h-4 mr-2" />
            {t('flyer.print', 'Imprimir')}
          </Button>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-300">
            <p className="font-medium mb-1">{t('flyer.tips_title', 'Consejos de uso')}</p>
            <ul className="space-y-1 text-blue-300/80">
              <li>• {t('flyer.tip1', 'Colócalo en tu escaparate o caja registradora')}</li>
              <li>• {t('flyer.tip2', 'Los clientes escanean y activan cupones al instante')}</li>
              <li>• {t('flyer.tip3', 'Cada escaneo te genera visibilidad en la plataforma')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlyerDownloadSection;
